// TGCLOUD-1669 Phase 5 — the section index in the promoted drawer.
//
// The pinned order becomes navigation instead of a scroll stack. That is the
// whole point of promoting: seventeen sections in one column is a scroll
// however wide the column is, and the complaint the ticket opens with is that
// "everything sits in one long scrolling panel".
//
// The order is `getUserSidebarItems` verbatim. It was agreed item by item with
// clinical users and is pinned by sidebarOrder.test.jsx, so promoting must not
// reorder it, and does not.

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const NAVY = '#2b3648';
const TEAL_AA = '#0e7186';
const MUTED = '#55606d';
const QUIET = '#5b6673';

export default function SectionIndex({
  items = [],
  counts = {},
  activeKey,
  onSelect
}) {
  const t = useAutoTranslation();

  return (
    <Box
      component="nav"
      aria-label={t('Sections')}
      sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1, pb: 2 }}
    >
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: '.8px',
          textTransform: 'uppercase',
          color: QUIET,
          px: 1,
          pt: 1.25,
          pb: 0.75
        }}
      >
        {t('Sections')}
        <Box component="span" sx={{ fontWeight: 600, ml: 0.75 }}>
          {items.length}
        </Box>
      </Typography>

      {items.map((item) => {
        const active = activeKey === item.value;
        const count = counts[item.value];
        return (
          <Box
            key={item.value}
            component="button"
            type="button"
            onClick={() => onSelect?.(item.value)}
            aria-current={active ? 'true' : undefined}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: '100%',
              border: 0,
              background: active ? '#e8f6f9' : 'transparent',
              // TEAL_AA, not the brand teal. This bar is the only NON-TEXT
              // marker of which section you are in, so WCAG 1.4.11 wants 3:1
              // against what it sits next to, and #2ba9bf managed 2.52:1
              // against this row's own tint. The label beside it was already
              // using the darkened tone for the same reason.
              borderLeft: `3px solid ${active ? TEAL_AA : 'transparent'}`,
              textAlign: 'left',
              px: 1,
              py: 0.625,
              borderRadius: '3px',
              cursor: 'pointer',
              '&:hover': { background: active ? '#e8f6f9' : '#eef3f6' }
            }}
          >
            <Box
              component="span"
              sx={{
                flex: 1,
                minWidth: 0,
                fontSize: 11.5,
                fontWeight: 800,
                letterSpacing: '.45px',
                color: active ? TEAL_AA : NAVY,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {t(item.label)}
            </Box>
            {/* A count only where one is known. `undefined` means the section
                has not reported yet, and a `0` there would read as a settled
                clinical absence — the same three-valued rule the summary lines
                follow. A real zero still shows, quietly. */}
            {count !== undefined && (
              <Box
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: count ? 750 : 500,
                  fontStyle: count ? 'normal' : 'italic',
                  color: count ? MUTED : QUIET,
                  fontVariantNumeric: 'tabular-nums',
                  flexShrink: 0
                }}
              >
                {count}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
